import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit } from 'lucide-react';
import {
  fetchTeamMemberAccessDetails,
  type TeamMemberAccessDetails,
} from '../../services/MasterAdmin/EmployeeManagementService';
import AdminProfileEditModal from '../../components/MasterAdmin/AdminProfileEditModal';
import { useAuth } from '../../hooks/useAuth';

type AuthSessionRow = TeamMemberAccessDetails['authActivity']['recentSessions'][number];
type FcmTokenRow = TeamMemberAccessDetails['fcmTokens'][number];
type ActivityLogRow = TeamMemberAccessDetails['activityLogs'][number];
type NotificationRow = TeamMemberAccessDetails['notifications'][number];

const formatDateTime = (dateString: string | null | undefined) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const maskToken = (token?: string | null) => {
  if (!token) return '—';
  if (token.length <= 16) return `${token.slice(0, 4)}…`;
  return `${token.slice(0, 10)}…${token.slice(-6)}`;
};

const ACTION_LABELS: Record<string, string> = {
  LOGIN: 'Logged in',
  LOGOUT: 'Logged out',
  PAGE_VIEW: 'Viewed page',
  FCM_REGISTERED: 'Registered push token',
  FCM_REFRESHED: 'Refreshed push token',
  PROFILE_UPDATED: 'Updated profile',
};

const getActivityPageName = (a: ActivityLogRow): string => {
  if (a.pageName?.trim()) return a.pageName.trim();
  const metaPage =
    a.meta && typeof a.meta.pageName === 'string' ? a.meta.pageName.trim() : '';
  if (metaPage) return metaPage;
  if (a.summary?.startsWith('Viewed page — ')) {
    return a.summary.slice('Viewed page — '.length).trim() || '—';
  }
  if (a.summary?.startsWith('Opened ')) {
    return a.summary.slice('Opened '.length).trim() || '—';
  }
  if (a.action === 'LOGIN' || a.action === 'LOGOUT') {
    return a.action === 'LOGIN' ? 'Login' : 'Logout';
  }
  return a.path || '—';
};

const getActivityPerformedAction = (a: ActivityLogRow): string => {
  if (a.performedAction?.trim()) return a.performedAction.trim();
  const metaAction =
    a.meta && typeof a.meta.performedAction === 'string'
      ? a.meta.performedAction.trim()
      : '';
  if (metaAction) return metaAction;
  return ACTION_LABELS[String(a.action || '').toUpperCase()] || a.action || '—';
};

const shortDeviceCode = (deviceId?: string | null): string => {
  const clean = String(deviceId || '').replace(/[^a-zA-Z0-9]/g, '');
  return (clean.slice(0, 8) || '').toUpperCase();
};

type DeviceIdentityFields = {
  deviceId?: string | null;
  deviceLabel?: string | null;
  deviceModel?: string | null;
  deviceCode?: string | null;
  browser?: string | null;
  os?: string | null;
  meta?: Record<string, unknown> | null;
};

const getDeviceDisplay = (row: DeviceIdentityFields) => {
  const code =
    (row.deviceCode && String(row.deviceCode).trim()) ||
    (row.meta && typeof row.meta.deviceCode === 'string'
      ? row.meta.deviceCode.trim()
      : '') ||
    shortDeviceCode(row.deviceId) ||
    '—';
  const model =
    (row.deviceModel && String(row.deviceModel).trim()) ||
    (row.meta && typeof row.meta.deviceModel === 'string'
      ? row.meta.deviceModel.trim()
      : '') ||
    '';
  const label =
    (row.deviceLabel && String(row.deviceLabel).trim()) ||
    (row.meta && typeof row.meta.deviceLabel === 'string'
      ? row.meta.deviceLabel.trim()
      : '') ||
    [row.os, row.browser].filter(Boolean).join(' · ') ||
    '';
  return {
    code,
    model,
    label,
    fullId: row.deviceId || '',
  };
};

const DeviceIdentityCell: React.FC<{ row: DeviceIdentityFields }> = ({ row }) => {
  const d = getDeviceDisplay(row);
  return (
    <div className="min-w-[11rem] max-w-[16rem]">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex rounded-md bg-indigo-50 px-2 py-0.5 font-mono text-xs font-semibold tracking-wide text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200"
          title="Unique device code — same laptop/browser keeps the same code"
        >
          {d.code}
        </span>
      </div>
      {d.model ? (
        <p className="mt-1 text-xs font-medium text-gray-900 dark:text-white">{d.model}</p>
      ) : null}
      {d.label ? (
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 line-clamp-2" title={d.label}>
          {d.label}
        </p>
      ) : (
        <p className="mt-0.5 text-xs text-gray-400">No device label yet</p>
      )}
      {d.fullId ? (
        <p className="mt-0.5 font-mono text-[10px] text-gray-400 break-all" title={d.fullId}>
          ID: {d.fullId}
        </p>
      ) : null}
    </div>
  );
};

type TabKey = 'overview' | 'auth' | 'fcm' | 'activity' | 'notifications';

const TeamMemberAccessPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, updateLocalUser } = useAuth();
  const [tab, setTab] = useState<TabKey>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeamMemberAccessDetails | null>(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);

  const reloadMember = async () => {
    if (!id) return;
    const res = await fetchTeamMemberAccessDetails(id);
    if (res.success && res.data) setData(res.data);
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchTeamMemberAccessDetails(id);
        if (!cancelled) {
          if (res.success && res.data) setData(res.data);
          else setError('Could not load team member details');
        }
      } catch {
        if (!cancelled) setError('Failed to load team member details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const member = data?.member;
  const auth = data?.authActivity;

  return (
    <div className="w-full min-w-0 space-y-6 p-3 sm:p-4 md:p-6">
      <button
        type="button"
        onClick={() => navigate('/employees')}
        className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
      >
        <ArrowLeft className="mr-1 h-4 w-4" /> Back to Team Access
      </button>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {!loading && !error && member && (
        <>
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {member.profile_image ? (
                <img
                  src={member.profile_image}
                  alt={member.name}
                  className="h-16 w-16 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-xl font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                  {(member.name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-2xl font-bold text-gray-900 dark:text-white">
                  {member.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-indigo-50 px-2.5 py-1 font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                    {member.role}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    {member.department || '—'}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    {member.status || 'ACTIVE'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowEditDrawer(true)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-yellow-600"
                title="Edit"
              >
                <Edit className="h-4 w-4" />
                Edit
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2 dark:border-gray-700">
            {(
              [
                ['overview', 'Overview'],
                ['auth', 'Auth activity'],
                ['fcm', 'FCM tokens'],
                ['activity', 'Activity'],
                ['notifications', 'Notifications'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  tab === key
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'overview' && auth && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {auth.isLoggedIn ? 'Logged in' : 'Logged out'}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500">Active sessions</p>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {auth.activeSessions}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500">Devices seen</p>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {auth.distinctDeviceCount}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500">Operator labels</p>
                <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  {auth.distinctOperatorLabels?.length
                    ? auth.distinctOperatorLabels.join(', ')
                    : '—'}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-gray-500">Last login</p>
                <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  {formatDateTime(auth.lastLogin)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-gray-500">Last logout</p>
                <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  {formatDateTime(auth.lastLogout)}
                </p>
              </div>
            </div>
          )}

          {tab === 'auth' && (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-4 py-3 text-left">Login</th>
                    <th className="px-4 py-3 text-left">Operator</th>
                    <th className="px-4 py-3 text-left">IP</th>
                    <th className="px-4 py-3 text-left">Browser / OS</th>
                    <th className="px-4 py-3 text-left">Device identity</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(auth?.recentSessions || []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No login sessions yet. Sessions appear after the next login.
                      </td>
                    </tr>
                  ) : (
                    (auth?.recentSessions || []).map((s: AuthSessionRow) => (
                      <tr key={s.id}>
                        <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(s.loginAt)}</td>
                        <td className="px-4 py-3">{s.operatorLabel || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{s.ipAddress || '—'}</td>
                        <td className="px-4 py-3">
                          {[s.browser, s.os].filter(Boolean).join(' / ') || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <DeviceIdentityCell row={s} />
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              s.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'fcm' && (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-4 py-3 text-left">User type</th>
                    <th className="px-4 py-3 text-left">Platform</th>
                    <th className="px-4 py-3 text-left">IP</th>
                    <th className="px-4 py-3 text-left">Browser / OS</th>
                    <th className="px-4 py-3 text-left">Token</th>
                    <th className="px-4 py-3 text-left">Active</th>
                    <th className="px-4 py-3 text-left">Last used</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(data?.fcmTokens || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        No FCM tokens registered for this user.
                      </td>
                    </tr>
                  ) : (
                    (data?.fcmTokens || []).map((t: FcmTokenRow) => (
                      <tr key={t.id}>
                        <td className="px-4 py-3">{t.userType || member.role}</td>
                        <td className="px-4 py-3">{t.platform}</td>
                        <td className="px-4 py-3 font-mono text-xs">{t.ipAddress || '—'}</td>
                        <td className="px-4 py-3">
                          {[t.browser, t.os].filter(Boolean).join(' / ') || '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{maskToken(t.fcmToken)}</td>
                        <td className="px-4 py-3">{t.isActive ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDateTime(t.lastUsedAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'activity' && (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-4 py-3 text-left">When</th>
                    <th className="px-4 py-3 text-left">Page</th>
                    <th className="px-4 py-3 text-left">Performed action</th>
                    <th className="px-4 py-3 text-left">Operator</th>
                    <th className="px-4 py-3 text-left">IP</th>
                    <th className="px-4 py-3 text-left">Device identity</th>
                    <th className="px-4 py-3 text-left">Path</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(data?.activityLogs || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        No activity logged yet.
                      </td>
                    </tr>
                  ) : (
                    (data?.activityLogs || []).map((a: ActivityLogRow) => (
                      <tr key={a.id}>
                        <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(a.createdAt)}</td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          {getActivityPageName(a)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-100">
                            {getActivityPerformedAction(a)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{a.operatorLabel || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{a.ipAddress || '—'}</td>
                        <td className="px-4 py-3">
                          <DeviceIdentityCell row={a} />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                          {a.path || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'notifications' && (
            <div className="space-y-3">
              {(data?.notifications || []).length === 0 ? (
                <p className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
                  No related notifications found for this role.
                </p>
              ) : (
                (data?.notifications || []).map((n: NotificationRow) => (
                  <div
                    key={n.id}
                    className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{n.title}</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{n.body}</p>
                        <p className="mt-2 text-xs text-gray-400">
                          {n.type} · {formatDateTime(n.createdAt)}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          n.isRead
                            ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}
                      >
                        {n.isRead ? 'Read' : 'Unread'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {member && (
        <AdminProfileEditModal
          open={showEditDrawer}
          onClose={() => setShowEditDrawer(false)}
          presentation="drawer"
          allowRoleEdit
          createdByUserId={user?.id || ''}
          member={{
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role,
            department: member.department,
            phone: member.phone,
            qualification: member.qualification,
            profile_image: member.profile_image,
          }}
          onSaved={async (saved) => {
            await reloadMember();
            if (user?.id && String(saved.id || '') === String(user.id)) {
              updateLocalUser({
                name: String(saved.name || ''),
                email: String(saved.email || ''),
                department: String(saved.department || ''),
                phone: String(saved.phone || ''),
                profile_image: String(saved.profile_image || ''),
                role: (saved.role as any) || user.role,
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default TeamMemberAccessPage;
